// Caixa offline do POS: abertura/fecho guardados neste dispositivo e conciliados com o servidor quando a ligação voltar
const KEY = "lumora_pos_offline_cash";
const newId = (p) => `${p}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } };
const write = (rec) => (rec ? localStorage.setItem(KEY, JSON.stringify(rec)) : localStorage.removeItem(KEY));

export const getOfflineCash = (companyId) => {
  const rec = read();
  return rec && (!companyId || !rec.company_id || rec.company_id === companyId) ? rec : null;
};
export const clearOfflineCash = () => write(null);

export const openCashOffline = ({ company_id, user_id, user_name, opening_balance, notes }) => {
  const rec = { company_id, open: { local_id: newId("offcash"), opening_balance, notes: notes || null, opened_at: new Date().toISOString(), user_id, user_name, synced: false, session_id: null, error: null }, close: null };
  write(rec);
  return rec;
};

export const closeCashOffline = (companyId, { closing_balance, notes, tpa_counts, expected_cash, tpa_expected, total_sales }) => {
  const rec = getOfflineCash(companyId) || { company_id: companyId, open: null, close: null };
  rec.close = { local_id: newId("offclose"), closing_balance, notes: notes || null, tpa_counts, closed_at: new Date().toISOString(), expected_cash_local: expected_cash, tpa_expected_local: tpa_expected, total_sales_local: total_sales, synced: false, error: null };
  write(rec);
  return rec;
};
export const discardOfflineCashClose = () => { const rec = read(); if (!rec) return; rec.close = null; write(rec.open && !rec.open.synced ? rec : rec.open ? rec : null); };
const patch = (part, p) => { const rec = read(); if (!rec || !rec[part]) return; rec[part] = { ...rec[part], ...p }; write(rec); };

export const hasPendingCashOpen = (rec) => !!(rec?.open && !rec.open.synced);
export const hasPendingCashClose = (rec) => !!(rec?.close && !rec.close.synced);

// Sessão de caixa "vista" pelo POS offline: base (abertura offline OU sessão do servidor em cache) + vendas offline ainda não sincronizadas
export const localCashSession = (base, rec, pendingSales, tpas) => {
  let s;
  if (hasPendingCashOpen(rec)) {
    const o = rec.open;
    s = { offline: true, status: "open", session_id: null, opened_at: o.opened_at, opened_by_name: o.user_name, opening_balance: o.opening_balance, total_sales: 0, total_cash: 0, total_card: 0, total_transfer: 0, total_mobile: 0, expected_cash: o.opening_balance,
      tpa_expected: (tpas || []).filter((t) => t.is_active !== false).map((t) => ({ tpa_id: t.tpa_id, name: t.name, bank_name: t.bank_name, expected: 0, count: 0, sales: 0, entries: 0 })) };
  } else if (base) {
    s = { ...base, tpa_expected: (base.tpa_expected || []).map((t) => ({ ...t })) };
  } else {
    return null;
  }
  const sales = (pendingSales || []).filter((x) => x.status !== "synced");
  for (const sale of sales) {
    const m = sale.payment?.method || "cash";
    const amt = Number(sale.total) || 0;
    s.total_sales = (s.total_sales || 0) + amt;
    s[`total_${m}`] = (s[`total_${m}`] || 0) + amt;
    if (m === "cash") s.expected_cash = (s.expected_cash || 0) + amt;
    if (m === "card" && sale.payment?.tpa_id) {
      let row = s.tpa_expected.find((t) => t.tpa_id === sale.payment.tpa_id);
      if (!row) { row = { tpa_id: sale.payment.tpa_id, name: sale.payment.tpa_name || "TPA", expected: 0, count: 0, sales: 0, entries: 0 }; s.tpa_expected.push(row); }
      row.expected += amt;
      row.count += 1;
      row.sales = (row.sales || 0) + 1;
    }
  }
  s.offline_sales_count = sales.length;
  return s;
};

const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : d?.message || `Erro ${e.response?.status}`; };

// 1º passo da sincronização: enviar a abertura offline (idempotente via client_ref). Devolve {ok, alreadyOpen, session, error, offline}
export const syncOfflineCashOpen = async (api, companyId) => {
  const rec = getOfflineCash(companyId);
  if (!hasPendingCashOpen(rec)) return { ok: true, skipped: true };
  try {
    const r = await api("post", "/cash/open", { opening_balance: rec.open.opening_balance, notes: rec.open.notes, client_ref: rec.open.local_id, opened_at: rec.open.opened_at });
    patch("open", { synced: true, session_id: r.data.session_id, error: null, already_open: !!r.data.already_open });
    return { ok: true, alreadyOpen: !!r.data.already_open, session: r.data };
  } catch (e) {
    if (!e.response) return { ok: false, offline: true };
    patch("open", { error: errMsg(e) });
    return { ok: false, error: errMsg(e) };
  }
};

// Último passo: fechar a caixa no servidor com os valores contados offline (o servidor calcula o esperado com todas as vendas já sincronizadas)
export const syncOfflineCashClose = async (api, companyId) => {
  const rec = getOfflineCash(companyId);
  if (!hasPendingCashClose(rec)) return { ok: true, skipped: true };
  const c = rec.close;
  try {
    const r = await api("post", "/cash/close", { closing_balance: c.closing_balance, notes: c.notes, tpa_counts: c.tpa_counts || [], client_ref: c.local_id, closed_at: c.closed_at });
    clearOfflineCash();
    return { ok: true, result: r.data, local: c };
  } catch (e) {
    if (!e.response) return { ok: false, offline: true };
    patch("close", { error: errMsg(e) });
    return { ok: false, error: errMsg(e) };
  }
};
